import {
  ChangeDetectorRef,
  Component,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { combineLatest, distinctUntilChanged, filter, Subject, switchMap, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';

import { provideAnalyticsViewContext } from '@core/analytics-legacy';
import { AnalyticsViewContext } from '@core/analytics-types';
import type { Requisition, RequisitionAssignment } from '@core/requisitions-types';
import { ProtectedControlsService, SpinnerComponent } from '@core/user-interface-legacy';
import { CustomFieldsValidationService } from '@recruitee/custom-fields-logic';

import { RequisitionsAnalytics } from '../../../analytics/requisitions.analytics';
import { RequisitionsDropAreaContainerComponent } from '../../../containers/single-requisition/requisitions-drop-area-container/requisitions-drop-area-container.component';
import { RequisitionsToolbarContainerComponent } from '../../../containers/single-requisition/requisitions-toolbar-container/requisitions-toolbar-container.component';
import { RequisitionsDataProviderService } from '../../../data/single-requisition/requisitions-data-provider.service';
import { RootPageDataService } from '../../../data/single-requisition/root-page-data.service';
import { CreateJobFacade } from '../../../facades/single-requisition/create-job.facade';
import { RequisitionsGeneralFacade } from '../../../facades/single-requisition/requisitions-general.facade';
import { TeamMembersFacade } from '../../../facades/single-requisition/team-members.facade';
import { RequisitionChannelService } from '../../../services/requisition-channel.service';
import { RequisitionFormManagerService } from '../../../services/requisition-form-manager.service';
import { RequisitionSaveService } from '../../../services/requisition-save.service';
import { RequisitionsNotificationsService } from '../../../services/requisitions-notifications.service';
import { RequisitionsSharedActionsService } from '../../../services/requisitions-shared-actions.service';
import { RequisitionsTutorialDialogService } from '../../../services/requisitions-tutorial-dialog.service';
import { SingleRequisitionsActionsService } from '../../../services/single-requisitions-actions.service';

@Component({
  selector: 'rt-requisitions-root-page',
  templateUrl: './root-page.component.html',
  styleUrls: ['./root-page.component.less'],
  imports: [
    RequisitionsDropAreaContainerComponent,
    RequisitionsToolbarContainerComponent,
    RouterModule,
    SpinnerComponent,
  ],
  host: {
    '(dragenter)': 'onDragEnter($event)',
    '(dragleave)': 'onDragLeave()',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    RequisitionsDataProviderService,
    RootPageDataService,
    SingleRequisitionsActionsService,
    RequisitionsSharedActionsService,
    TeamMembersFacade,
    RequisitionsGeneralFacade,
    CreateJobFacade,
    RequisitionsAnalytics,
    RequisitionFormManagerService,
    RequisitionSaveService,
    RequisitionChannelService,
    CustomFieldsValidationService,
    provideAnalyticsViewContext(AnalyticsViewContext.Requisition),
  ],
})
export class RootPageComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly rootData = inject(RootPageDataService);
  private readonly tutorialDialogService = inject(RequisitionsTutorialDialogService);
  private readonly vcRef = inject(ViewContainerRef);
  private readonly router = inject(Router);
  private readonly protectedControls = inject(ProtectedControlsService);
  private readonly requisitionChannel = inject(RequisitionChannelService);
  private readonly createJobFacade = inject(CreateJobFacade);

  protected readonly requisitionsNotificationsService = inject(RequisitionsNotificationsService);
  protected readonly requisitionsData = inject(RequisitionsDataProviderService);

  public requisition: Requisition;
  public assignments: RequisitionAssignment[];
  public isLoaded = false;

  public dragEventData: DragEvent | null = null;

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.tutorialDialogService.openIntroDialogIfNeeded(this.vcRef);

    combineLatest([this.rootData.requisition$, this.rootData.assignments$])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([requisition, assignments]) => {
        this.requisition = requisition;
        this.assignments = assignments;
        this.requisitionChannel.subscribeToWebSocketsChannel(requisition.id);
        this.cdRef.detectChanges();
      });

    this.rootData.isLoaded$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(isLoaded => {
      this.isLoaded = isLoaded;
      this.cdRef.detectChanges();
    });

    this.listenToRequisitionFeatureDisable();
    this.subscribeToWebSocketOfferChannel();
    this.listenToRequisitionStatusChange();
  }

  public ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public onDragEnter($event: DragEvent): void {
    this.dragEventData = $event;
    this.cdRef.detectChanges();
  }

  public onDragLeave(): void {
    this.dragEventData = null;
    this.cdRef.detectChanges();
  }

  private listenToRequisitionFeatureDisable() {
    this.rootData.isFeatureEnabled$
      .pipe(
        filter(isEnabled => !isEnabled),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        this.protectedControls.discard();
        this.router.navigate(['/dashboard']);
      });
  }

  private subscribeToWebSocketOfferChannel() {
    this.rootData.requisition$
      .pipe(
        map(requisition => requisition.id),
        distinctUntilChanged(),
        switchMap(requisitionId =>
          this.requisitionChannel.subscribeToWebSocketsChannel(requisitionId),
        ),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe();
  }

  private listenToRequisitionStatusChange() {
    this.rootData.requisition$
      .pipe(
        map(requisition => requisition.status),
        distinctUntilChanged(),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => this.createJobFacade.handleShouldShowApprovedDialog());
  }
}
